import GitHubIcon from "@mui/icons-material/GitHub";
import GitLabIcon from "@mui/icons-material/GitHub"; // Reuse GitHub icon for GitLab placeholder
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

// ── Shared VCS action buttons ────────────────────────────────────────────────

interface VcsActionButtonsProps {
  installing: boolean;
  gitLabAuthing: boolean;
  polling: boolean;
  hasActiveGithub: boolean;
  hasActiveGitLab: boolean;
  onInstall: () => void;
  onGitLabAuth: () => void;
  variant?: "header" | "empty" | "inline";
}

export function VcsActionButtons({
  installing,
  gitLabAuthing,
  polling,
  hasActiveGithub,
  hasActiveGitLab,
  onInstall,
  onGitLabAuth,
  variant = "header",
}: VcsActionButtonsProps) {
  const isInline = variant === "inline";
  const isEmpty = variant === "empty";

  const githubBtn = !hasActiveGithub && !polling && !gitLabAuthing && (
    <Button
      variant={isEmpty ? "contained" : "text"}
      size={isInline ? "small" : undefined}
      startIcon={
        installing ? (
          <CircularProgress size={isInline ? 14 : 16} color="inherit" />
        ) : (
          <GitHubIcon fontSize={isInline ? "small" : undefined} />
        )
      }
      endIcon={
        !installing && !isInline ? (
          <OpenInNewIcon fontSize="small" />
        ) : undefined
      }
      onClick={onInstall}
      disabled={installing}
      sx={{ whiteSpace: "nowrap" }}
    >
      {installing
        ? "Opening GitHub…"
        : isInline
          ? "Connect another organization (GitHub)"
          : "Install GitHub App"}
    </Button>
  );

  const gitlabBtn = !hasActiveGitLab && (
    <Button
      variant={isEmpty ? "outlined" : "text"}
      size={isInline ? "small" : undefined}
      startIcon={
        gitLabAuthing ? (
          <CircularProgress size={isInline ? 14 : 16} color="inherit" />
        ) : (
          <GitLabIcon fontSize={isInline ? "small" : undefined} />
        )
      }
      endIcon={
        !gitLabAuthing && !isInline ? (
          <OpenInNewIcon fontSize="small" />
        ) : undefined
      }
      onClick={onGitLabAuth}
      disabled={gitLabAuthing}
      sx={{ whiteSpace: "nowrap", ...(isInline ? { ml: 2 } : {}) }}
    >
      {gitLabAuthing
        ? "Opening GitLab…"
        : isInline
          ? "Connect another organization (GitLab)"
          : "Connect GitLab"}
    </Button>
  );

  if (isInline) {
    return (
      <Box>
        {githubBtn}
        {gitlabBtn}
      </Box>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent={isEmpty ? "center" : undefined}
    >
      {githubBtn}
      {gitlabBtn}
    </Stack>
  );
}

// ── GitHub App install / empty-state card ─────────────────────────────────────

interface GitHubAppInstallCardProps {
  installing: boolean;
  gitLabAuthing: boolean;
  polling: boolean;
  hasActiveGithub: boolean;
  hasActiveGitLab: boolean;
  onInstall: () => void;
  onGitLabAuth: () => void;
}

export function GitHubAppInstallCard({
  installing,
  gitLabAuthing,
  polling,
  hasActiveGithub,
  hasActiveGitLab,
  onInstall,
  onGitLabAuth,
}: GitHubAppInstallCardProps) {
  return (
    <Card variant="outlined" sx={{ textAlign: "center", py: 6 }}>
      <CardContent>
        <GitHubIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          No VCS connection yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Connect GitHub or GitLab to enable automatic repository provisioning
          and push-triggered deployments.
        </Typography>
        <VcsActionButtons
          installing={installing}
          gitLabAuthing={gitLabAuthing}
          polling={polling}
          hasActiveGithub={hasActiveGithub}
          hasActiveGitLab={hasActiveGitLab}
          onInstall={onInstall}
          onGitLabAuth={onGitLabAuth}
          variant="empty"
        />
      </CardContent>
    </Card>
  );
}
